import { Component, OnInit, ChangeDetectorRef, ViewChild, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import { Group, SchedulerComponent, EventClickEvent } from "@progress/kendo-angular-scheduler";
import { DialogService } from "@progress/kendo-angular-dialog";
import { EventDetailsModalComponent } from "./event-details-modal/event-details-modal.component";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  IApiUser,
  IApiUserCalendar,
  IApiUserCalendarFilter,
  IApiUserCalendarFilterType,
} from "src/app/shared/modules/graphql/types/types";
import { UserService } from "src/app/shared/services";
import { DistanceSelectionPayload } from "../calendar-filters/calendar-user-distance-filter/calendar-user-distance-filter.component";

dayjs.extend(isoWeek);

const USER_COLORS: string[] = [
  "#283593", "#C62828", "#2E7D32", "#1565C0", "#4527A0",
  "#AD1457", "#6A1B9A", "#00695C", "#EF6C00", "#0277BD",
  "#37474F", "#4E342E", "#00838F", "#D84315", "#558B2F",
  "#1A237E", "#880E4F", "#4A148C", "#004D40", "#311B92",
];

@Component({
  selector: "app-user-calendar",
  templateUrl: "./user-calendar.component.html",
  styleUrls: ["./user-calendar.component.scss"],
})
export class UserCalendarComponent implements OnInit, OnChanges {
  @ViewChild("scheduler") public scheduler!: SchedulerComponent;

  @Input() public userIds: string[] = [];
  @Input() public additionalFilters: IApiUserCalendarFilter[] = [];
  @Input() public distanceSelection: DistanceSelectionPayload = {
    userIds: [],
    metrics: [],
  };
  @Input() public showFilters: boolean = false;

  @Output() public dateChange = new EventEmitter<Date>();
  @Output() public viewChange = new EventEmitter<number>();
  @Output() public loadingChange = new EventEmitter<boolean>();

  public selectedDate: Date = new Date();
  public selectedViewIndex = 0;
  public loading = false;

  public startDate!: Date;
  public endDate!: Date;

  public group: Group = {
    resources: ["Employees"],
    orientation: "vertical",
  };

  public resources: any[] = [
    {
      name: "Employees",
      data: [],
      field: "employeeId",
      textField: "text",
      valueField: "value",
      colorField: "color",
    },
  ];

  public events: any[] = [];

  public startTime: string = "05:00";
  public endTime: string = "19:00";
  public showCalendar = true;

  constructor(
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
  ) { }

  ngOnInit(): void {
    this.updateRange();
    this.loadCalendarEvents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.showFilters) {
      if (changes['userIds']) {
        this.loadCalendarEvents();
      }
      if (changes['distanceSelection']) {
        this.onDistanceSelectionChange(this.distanceSelection);
      }
    } else {
      if (changes['additionalFilters']) {
        this.loadCalendarEvents();
      }
    }
  }

  public updateRange() {
    const d = dayjs(this.selectedDate);

    switch (this.selectedViewIndex) {
      case 0:
        this.startDate = d.startOf("day").toDate();
        this.endDate = d.endOf("day").toDate();
        break;
      case 1:
        this.startDate = d.startOf("week").toDate();
        this.endDate = d.startOf("week").add(2, "week").subtract(1, "day").endOf("day").toDate();
        break;
      case 2:
        this.startDate = d.startOf("month").toDate();
        this.endDate = d.endOf("month").toDate();
        break;
      case 3:
        this.startDate = d.startOf("week").toDate();
        this.endDate = d.endOf("week").toDate();
        break;
    }
  }

  public onDateChange(event: any): void {
    const newDate = event?.selectedDate || this.selectedDate;
    if (dayjs(this.selectedDate).isSame(dayjs(newDate), 'day')) {
      return;
    }
    this.selectedDate = newDate;
    this.updateRange();
    this.loadCalendarEvents();
    this.dateChange.emit(this.selectedDate);
  }

  public onViewIndexChange(index: number): void {
    if (this.selectedViewIndex === index) {
      return;
    }
    this.selectedViewIndex = index;
    this.updateRange();
    this.loadCalendarEvents();
    this.viewChange.emit(index);
  }

  public onDistanceSelectionChange(payload: DistanceSelectionPayload): void {
    this.distanceSelection = payload || { userIds: [], metrics: [] };
    this.resources = this.applyDistanceMetricsToResources(this.resources);
    this.events = this.events.map((event: any) => {
      const metric = this.getDistanceMetricByUserId(event.userId);
      return {
        ...event,
        distance: metric?.distance,
        duration: metric?.duration,
      };
    });
    this.cdr.markForCheck();
  }

  public formatDistance(distance?: number): string {
    if (distance === undefined || distance === null || Number.isNaN(distance) || distance === Number.POSITIVE_INFINITY) {
      return "";
    }
    return `${distance.toFixed(1)} mi`;
  }

  public formatDuration(duration?: string | number): string {
    if (duration === undefined || duration === null || `${duration}`.trim() === "") {
      return "";
    }
    return `${duration}`;
  }

  public loadCalendarEvents(): void {
    if (!this.userIds?.length && !this.additionalFilters?.length) {
      this.events = [];
      this.resources = this.emptyEmployeeResources();
      this.reRenderCalendar()
      return;
    }

    this.updateRange();
    this.loading = true;
    this.loadingChange.emit(true);

    this.userService.getUserCalendarConnection(this.buildFilterInput()).subscribe(
      (response) => {
        const previouslyEmpty = !this.events.length;
        this.resources = this.buildEmployeeResources(response);
        this.events = this.mapCalendarEvents(response);
        this.loading = false;
        this.loadingChange.emit(false);

        const nowHasEvents = this.events.length > 0;

        if ([0, 3].includes(this.selectedViewIndex) && previouslyEmpty && nowHasEvents) {
          this.reRenderCalendar();
        } else {
          this.cdr.detectChanges();
        }
      },
      () => {
        this.resources = this.emptyEmployeeResources();
        this.events = [];
        this.loading = false;
        this.loadingChange.emit(false);
      }
    );
  }

  private buildFilterInput(): IApiUserCalendarFilter[] {
    const filters: IApiUserCalendarFilter[] = [
      ...this.additionalFilters,
      {
        type: IApiUserCalendarFilterType.Timezone,
        value: "America/New_York",
      },
      {
        type: IApiUserCalendarFilterType.DateRange,
        value: JSON.stringify({
          start: this.startDate.toISOString(),
          end: this.endDate.toISOString(),
        }),
      },
    ];

    if (this.userIds?.length) {
      filters.push({
        type: IApiUserCalendarFilterType.UserIdIn,
        value: JSON.stringify(this.userIds),
      });
    }

    return filters;
  }

  private mapCalendarEvents(response: IApiUserCalendar[]): any[] {
    const events = (response || []).flatMap((userCalendar) => {
      const user = userCalendar?.user;
      const userId = `${user?.id || ""}`;
      const userName = this.getUserName(user);
      const cityState = this.getCityState(user);
      const distanceMetric = this.getDistanceMetricByUserId(userId);

      return this.parseUserEvents(userCalendar?.events).map((event: any, index: number) => {
        const start = this.toDate(event?.start?.dateTime);
        const end = this.toDate(event?.end?.dateTime) ||
          (start ? dayjs(start).add(1, "hour").toDate() : null);
        if (!start || !end) {
          return null;
        }

        return {
          id: event?.id || `${userId}-${start.getTime()}-${index}`,
          title: event?.subject || "(No subject)",
          start,
          end,
          userId,
          employeeId: userId,
          userName,
          cityState,
          distance: distanceMetric?.distance,
          duration: distanceMetric?.duration,
          body: event?.body
        };
      });
    });

    return events.filter(Boolean).sort((a: any, b: any) => a.start.getTime() - b.start.getTime());
  }

  private parseUserEvents(events: any): any[] {
    if (Array.isArray(events)) return events;
    if (typeof events === "string") {
      try {
        const parsedEvents = JSON.parse(events);
        return Array.isArray(parsedEvents) ? parsedEvents : [];
      } catch { return []; }
    }
    return [];
  }

  private toDate(value?: string): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getUserName(user?: IApiUser | null): string {
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
    return name || "-";
  }

  private getCityState(user?: IApiUser | null): string {
    const city = user?.HomeAddress?.city || "";
    const state = user?.HomeAddress?.state || "";
    const cityState = [city, state].filter(Boolean).join(", ").trim();
    return cityState || "-";
  }

  private getDistanceMetricByUserId(userId: string): { id: string; distance?: number; duration?: number } | undefined {
    if (!userId) return undefined;
    return this.distanceSelection?.metrics?.find((metric) => `${metric.id}` === `${userId}`);
  }

  private getCertifications(user?: any): string[] {
    const certs = user?.CertForCalView || [];
    return certs.map((cert: any) => cert?.Type?.title).filter(Boolean);
  }

  private buildEmployeeResources(response: IApiUserCalendar[]): any[] {
    const users = (response || [])
      .map((userCalendar) => userCalendar?.user)
      .filter(Boolean) as IApiUser[];

    const uniqueUsers = Array.from(new Map(users.map((user) => [`${user.id}`, user])).values());

    const data = uniqueUsers.map((user, index) => ({
      text: this.getUserName(user),
      value: `${user.id}`,
      cityState: this.getCityState(user),
      certifications: this.getCertifications(user),
      ...this.getDistanceMetricByUserId(`${user.id}`),
      color: USER_COLORS[index % USER_COLORS.length],
      departmentId: index + 1,
    })).sort((a: any, b: any) => {
      const distA = a.distance ?? Infinity;
      const distB = b.distance ?? Infinity;
      if (distA !== distB) return distA - distB;
      return (a.text || '').localeCompare(b.text || '');
    });

    return [{
      name: "Employees",
      data: data || [],
      field: "employeeId",
      textField: "text",
      valueField: "value",
      colorField: "color",
    }];
  }

  private emptyEmployeeResources(): any[] {
    return [{
      name: "Employees",
      data: [],
      field: "employeeId",
      textField: "text",
      valueField: "value",
      colorField: "color",
    }];
  }

  private applyDistanceMetricsToResources(resources: any[]): any[] {
    if (!Array.isArray(resources)) return this.emptyEmployeeResources();
    return resources.map((resource: any) => {
      if (!Array.isArray(resource?.data)) return resource;
      return {
        ...resource,
        data: resource.data.map((employee: any) => {
          const metric = this.getDistanceMetricByUserId(`${employee?.value || ""}`);
          return {
            ...employee,
            distance: metric?.distance,
            duration: metric?.duration,
          };
        }).sort((a: any, b: any) => {
          const distA = a.distance ?? Infinity;
          const distB = b.distance ?? Infinity;
          if (distA !== distB) return distA - distB;
          return (a.text || '').localeCompare(b.text || '');
        }),
      };
    });
  }

  public getSlotClass = (args: any) => {
    const date = args.start;
    if (date.toLocaleDateString() === this.selectedDate.toLocaleDateString()) {
      return { highlightDate: true };
    }
    if (dayjs(date).isBefore(dayjs(this.selectedDate))) {
      return { disabledSlot: true };
    }
    return {};
  };

  public onEventClick(args: EventClickEvent): void {
    const event = args.event;
    const dialogRef = this.dialogService.open({
      content: EventDetailsModalComponent,
      width: 600,
    });
    const instance = dialogRef.content.instance as EventDetailsModalComponent;
    instance.event = event;
  }

  private reRenderCalendar(): void {
    this.showCalendar = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showCalendar = true;
      this.cdr.detectChanges();
    }, 0);
  }
}
